import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Dialog } from '@headlessui/react';

import { Button, Link } from '@/atoms';

import ExitPopUp from '@/assets/icons/ExitPopUp.svg';
import Countdown from '@/components/atoms/countdown/Countdown';

import type { FunctionComponent } from 'react';
import type { TypePopUpOpenPageProps } from './types';

/**
 * PopUpOpenPage
 */
export const PopUpOpenPage: FunctionComponent<TypePopUpOpenPageProps> = ({
  className = '',
  title,
  button,
  desc,
  image,
  link,
  idPopUp,
  buttonClosesPopup,
  countdown,
  countdownTitle,
  countdownSubtitle,
  autoApertura,
}: TypePopUpOpenPageProps) => {
  const [popUpOpen, setPopUpOpen] = useState(false);
  const panel = useRef(null);

  useEffect(() => {
    const isModalShown = window.sessionStorage.getItem(idPopUp);

    if (isModalShown !== 'Shown') {
      setPopUpOpen(true);
      sessionStorage.setItem(idPopUp, 'Shown');
    } else {
      autoApertura ? setPopUpOpen(true) : setPopUpOpen(false);
    }
  }, []);

  const togglePopUp = () => {
    setPopUpOpen(!popUpOpen);
  };

  return (
    <Dialog
      open={popUpOpen}
      // eslint-disable-next-line
      onClose={() => {}}
      className="absolute inset-0 z-50 grid place-items-center"
    >
      <Dialog.Overlay
        className="absolute inset-0 bg-[rgba(21,33,39,0.7)]"
        onClick={togglePopUp}
      />

      <Dialog.Panel
        className={`popup-container z-50 flex h-fit w-[calc(100vw-2rem)] flex-col items-center overflow-hidden bg-[#F9F0EB] font-primary lg:w-[1080px] lg:flex-row ${className}`}
      >
        {image && (
          <div className="popup-img-container">
            <Image layout="fill" src={image} className="object-cover" />
            <div className="popup-img">
              <ExitPopUp
                height={32}
                width={32}
                className="flex-shrink-0 hover:cursor-pointer"
                onClick={togglePopUp}
              />
            </div>
          </div>
        )}

        <div ref={panel} className="popup-text-container relative text-center">
          <div
            className={`flex h-full flex-col justify-evenly ${
              image ? '' : 'px-10 md:px-[100px]'
            }`}
          >
            <div className="absolute right-2 top-2 flex justify-end">
              <ExitPopUp
                height={32}
                width={32}
                className="flex-shrink-0 hover:cursor-pointer"
                onClick={togglePopUp}
              />
            </div>
            <div className="flex flex-col justify-center gap-3 md:gap-6 ">
              <div className="flex flex-col gap-6">
                <div className="text-[32px] font-bold leading-[36px] md:text-[48px] md:leading-[52px]">
                  {title}
                </div>
                {desc && (
                  <div
                    className="popup-desc text-[14px] md:text-[20px]"
                    dangerouslySetInnerHTML={{ __html: desc }}
                  />
                )}
              </div>
              <div className="mb-[2rem] lg:mb-0">
                <Countdown
                  countdown={countdown}
                  title={countdownTitle}
                  subtitle={countdownSubtitle}
                />
              </div>
            </div>
            <div className="flex flex-col flex-wrap content-center justify-between gap-3">
              {button.label &&
                (buttonClosesPopup ? (
                  <Button
                    className="popup-buttons whitespace-nowrap"
                    size="large"
                    variant={button.variant}
                    onClick={togglePopUp}
                  >
                    {button.label}
                  </Button>
                ) : (
                  <Button
                    className="popup-buttons whitespace-nowrap"
                    size="large"
                    as="a"
                    href={button.src}
                    variant={button.variant}
                  >
                    {button.label}
                  </Button>
                ))}
              {link.label && (
                <Link
                  arrow
                  className="mx-auto flex flex justify-center self-center md:w-[50%] md:items-center md:self-start md:pl-2 md:pt-3"
                  variant={link.variant}
                  href={link.src ?? '#'}
                >
                  {link.label}
                </Link>
              )}
            </div>
          </div>
        </div>
      </Dialog.Panel>
    </Dialog>
  );
};

PopUpOpenPage.displayName = 'PopUpOpenPage';
